/**
 * Chat panel for the Insights Playground — conversational AI assistant
 * that understands the parquet schemas, SQL, and soccer analytics domain.
 *
 * Chat sessions auto-save to the server on every assistant turn. A session
 * picker lets you resume previous conversations or start new ones. Sessions
 * are scoped per game_code so the list filters naturally.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { trackEvent } from '../lib/analytics'
import { api, ApiError } from '../lib/api'
import { cachedTables, fetchTables } from '../lib/tablesCache'
import { createTempTable } from '../lib/projects'

type Message = {
  role: 'user' | 'assistant'
  content: string
  sql?: string
  explanation?: string
  visual?: string
  suggestions?: string[]
  /** When set, this message is a temp-table confirmation with a usable name. */
  tempTable?: string
}

type TableColumn = { name: string; type: string; description?: string | null }
type TableInfo = { name: string; columns?: TableColumn[] }

type SessionSummary = {
  id: number
  title: string
  game_code: string | null
  message_count: number
  updated_at: string | null
}

type SessionFull = {
  id: number
  title: string
  game_code: string | null
  org_id: number | null
  messages: Message[]
}

export default function ChatPanel({
  gameCode,
  orgId,
  projectId,
  onUseQuery,
  onAddCard,
}: {
  gameCode: string | null
  orgId: number | null
  /** Active project id — needed for temp table scoping. */
  projectId?: number | null
  onUseQuery?: (sql: string, visual?: string) => void
  /** Collaboration mode: create a canvas card from the chat result. */
  onAddCard?: (question: string, sql: string, visual?: string, explanation?: string) => void
}) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Auto-run mode: the assistant's query is applied the moment it arrives
  // (a canvas card on the collaborate tab, the editor elsewhere) instead of
  // waiting for the per-message button. Sticky per browser.
  const [autoRun, setAutoRun] = useState(
    () => localStorage.getItem('pg-chat-autorun') === '1')
  const toggleAutoRun = useCallback(() => {
    setAutoRun((v) => {
      localStorage.setItem('pg-chat-autorun', v ? '0' : '1')
      return !v
    })
  }, [])
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Available tables (cached per game)
  const [tables, setTables] = useState<TableInfo[]>([])
  const [showTables, setShowTables] = useState(false)
  const [expandedTable, setExpandedTable] = useState<string | null>(null)

  // Session state
  const [sessionId, setSessionId] = useState<number | null>(null)
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [showPicker, setShowPicker] = useState(false)

  // Fetch available tables when game changes
  useEffect(() => {
    if (!gameCode) { setTables([]); return }
    let alive = true
    const seen = cachedTables<{ tables: TableInfo[] }>(gameCode, orgId ?? '')
    if (seen) setTables(seen.tables ?? [])
    fetchTables<{ tables: TableInfo[] }>(gameCode, orgId ?? '').then((r) => {
      if (alive) setTables(r.tables ?? [])
    }).catch(() => {
      if (alive && !seen) setTables([])
    })
    return () => { alive = false }
  }, [gameCode, orgId])

  // Load session list when game changes
  useEffect(() => {
    if (!gameCode) { setSessions([]); return }
    let alive = true
    api<{ sessions: SessionSummary[] }>(
      `/api/v1/playground/chat-sessions?game_code=${encodeURIComponent(gameCode)}`,
    ).then((r) => {
      if (alive) setSessions(r.sessions)
    }).catch(() => {
      if (alive) setSessions([])
    })
    return () => { alive = false }
  }, [gameCode])

  // When game changes, reset to a blank chat (user can pick a previous one)
  useEffect(() => {
    setSessionId(null)
    setMessages([])
    setError(null)
  }, [gameCode])

  // Load a session's messages
  const loadSession = useCallback(async (id: number) => {
    try {
      const r = await api<{ session: SessionFull }>(
        `/api/v1/playground/chat-sessions/${id}`,
      )
      setSessionId(r.session.id)
      setMessages(r.session.messages || [])
      setShowPicker(false)
      setError(null)
    } catch {
      setError('Could not load session')
    }
  }, [])

  // Create a new server session, returns its id
  const createSession = useCallback(async (firstUserMsg: string, msgs: Message[]): Promise<number | null> => {
    try {
      const title = firstUserMsg.length > 80
        ? firstUserMsg.slice(0, 77) + '...'
        : firstUserMsg
      const r = await api<{ session: SessionFull }>('/api/v1/playground/chat-sessions', {
        method: 'POST',
        body: JSON.stringify({
          title,
          game_code: gameCode,
          org_id: orgId,
          messages: msgs,
        }),
      })
      return r.session.id
    } catch {
      return null
    }
  }, [gameCode, orgId])

  // Save messages to existing session
  const saveMessages = useCallback(async (id: number, msgs: Message[]) => {
    try {
      await api(`/api/v1/playground/chat-sessions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ messages: msgs }),
      })
    } catch {
      // Best-effort save
    }
  }, [])

  // Auto-scroll on new messages
  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight)
  }, [messages])

  const send = useCallback(async () => {
    const text = input.trim()
    if (!text || sending || !gameCode) return

    const userMsg: Message = { role: 'user', content: text }
    const updated = [...messages, userMsg]
    setMessages(updated)
    setInput('')
    setSending(true)
    setError(null)

    try {
      // The one seam every Lab chat message rides through; the message
      // content is customer material and stays out of the properties.
      trackEvent('Insights Lab Chat Sent', { game_code: gameCode })
      const resp = await api<{
        reply: string
        sql?: string
        explanation?: string
        visual?: string
        suggestions?: string[]
        source: string
      }>('/api/v1/playground/chat', {
        method: 'POST',
        body: JSON.stringify({
          game_code: gameCode,
          org_id: orgId,
          messages: updated.map((m) => ({ role: m.role, content: m.content })),
        }),
      })

      const assistantMsg: Message = {
        role: 'assistant',
        content: resp.reply,
        sql: resp.sql,
        explanation: resp.explanation,
        visual: resp.visual,
        suggestions: resp.suggestions,
      }
      let allMsgs = [...updated, assistantMsg]
      setMessages(allMsgs)

      // Auto-run mode: apply the query now — same action as the message's
      // own button. Temp-table answers keep their dedicated flow below.
      if (autoRun && resp.sql && resp.visual !== 'temp_table') {
        if (onAddCard) onAddCard(text, resp.sql, resp.visual, resp.explanation)
        else onUseQuery?.(resp.sql, resp.visual)
      }

      // Auto-create temp table when the assistant suggests it
      if (resp.visual === 'temp_table' && resp.sql && gameCode && orgId && projectId) {
        try {
          const tt = await createTempTable({
            game_code: gameCode, org_id: orgId,
            sql: resp.sql, project_id: projectId,
            label: resp.explanation || text.slice(0, 60),
          })
          const ttMsg: Message = {
            role: 'assistant',
            content: `Saved as \`${tt.name}\` (${tt.row_count} rows, expires in ${Math.round(tt.expires_in_s / 60)} min).`,
            tempTable: tt.name,
          }
          allMsgs = [...allMsgs, ttMsg]
          setMessages(allMsgs)
        } catch {
          // Best-effort — the original answer is still useful
        }
      }

      // Auto-save: create session on first turn, update on subsequent turns
      let sid = sessionId
      if (!sid) {
        sid = await createSession(text, allMsgs)
        if (sid) {
          setSessionId(sid)
          // Refresh the session list
          setSessions((prev) => [{
            id: sid!,
            title: text.length > 80 ? text.slice(0, 77) + '...' : text,
            game_code: gameCode,
            message_count: allMsgs.length,
            updated_at: new Date().toISOString(),
          }, ...prev])
        }
      } else {
        void saveMessages(sid, allMsgs)
        // Update the count in the local list
        setSessions((prev) => prev.map((s) =>
          s.id === sid ? { ...s, message_count: allMsgs.length, updated_at: new Date().toISOString() } : s,
        ))
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message
        : e instanceof TypeError ? 'Connection error — is the API reachable?'
        : String(e)
      setError(msg)
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }, [input, sending, gameCode, orgId, projectId, messages, sessionId,
      createSession, saveMessages, autoRun, onAddCard, onUseQuery])

  const handleKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        void send()
      }
    },
    [send],
  )

  const startNewChat = useCallback(() => {
    setSessionId(null)
    setMessages([])
    setError(null)
    setShowPicker(false)
  }, [])

  const deleteSession = useCallback(async (id: number) => {
    try {
      await api(`/api/v1/playground/chat-sessions/${id}`, { method: 'DELETE' })
      setSessions((prev) => prev.filter((s) => s.id !== id))
      if (sessionId === id) {
        setSessionId(null)
        setMessages([])
      }
    } catch {
      // Best-effort delete
    }
  }, [sessionId])

  return (
    <div className="pg-chat">
      <div className="pg-chat__header">
        <h3>Analytics Assistant</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {(onAddCard || onUseQuery) && (
            <label className="muted"
                   style={{ fontSize: 11, display: 'flex', gap: 4,
                            alignItems: 'center', cursor: 'pointer' }}
                   title={onAddCard
                     ? 'Run each answer immediately as a canvas card — no "Add as card" click'
                     : 'Apply each answer to the editor immediately — no "Use this query" click'}>
              <input type="checkbox" checked={autoRun} onChange={toggleAutoRun} />
              Auto-run
            </label>
          )}
          {tables.length > 0 && (
            <button
              className="linklike"
              onClick={() => { setShowTables(!showTables); setShowPicker(false) }}
            >
              {showTables ? 'Hide' : `Tables (${tables.length})`}
            </button>
          )}
          {sessions.length > 0 && (
            <button
              className="linklike"
              onClick={() => { setShowPicker(!showPicker); setShowTables(false) }}
            >
              {showPicker ? 'Hide' : 'History'}
            </button>
          )}
          <button className="linklike" onClick={startNewChat}>
            New chat
          </button>
        </div>
      </div>

      {showTables && tables.length > 0 && (
        <div className="pg-chat__sessions" style={{ maxHeight: 280 }}>
          {tables.map((t) => (
            <div key={t.name} style={{ fontSize: 12 }}>
              <button
                className="pg-chat__session-title"
                style={{ fontFamily: 'var(--font-mono, monospace)',
                         fontSize: 11, padding: '3px 6px', width: '100%',
                         textAlign: 'left', background: 'none', border: 'none',
                         cursor: 'pointer',
                         color: expandedTable === t.name
                           ? 'var(--color-text-primary, #333)'
                           : 'var(--color-text-secondary, #666)' }}
                onClick={() => setExpandedTable(expandedTable === t.name ? null : t.name)}
                title={t.columns ? `${t.columns.length} columns` : t.name}
              >
                {expandedTable === t.name ? '\u25BC' : '\u25B6'} {t.name}
                {t.columns && (
                  <span className="muted" style={{ marginLeft: 4 }}>
                    ({t.columns.length} cols)
                  </span>
                )}
              </button>
              {expandedTable === t.name && t.columns && (
                <div style={{ paddingLeft: 18, paddingBottom: 4 }}>
                  {t.columns.map((c) => (
                    <div key={c.name} title={c.description ?? undefined}
                         style={{ fontFamily: 'var(--font-mono, monospace)',
                                               fontSize: 10, lineHeight: 1.6,
                                               color: 'var(--color-text-secondary, #888)' }}>
                      {c.name} <span className="muted">{c.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showPicker && sessions.length > 0 && (
        <div className="pg-chat__sessions">
          {sessions.map((s) => (
            <div
              key={s.id}
              className={`pg-chat__session-row${s.id === sessionId ? ' pg-chat__session-row--active' : ''}`}
            >
              <button
                className="pg-chat__session-title"
                onClick={() => void loadSession(s.id)}
                title={s.title}
              >
                {s.title}
                <span className="muted"> ({s.message_count})</span>
              </button>
              <button
                className="pg-chat__session-delete"
                onClick={() => void deleteSession(s.id)}
                title="Delete session"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="pg-chat__messages" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="pg-chat__empty">
            <p>Ask questions about this game's data.</p>
            <p className="muted">
              Try: &ldquo;How many possessions ended in a shot?&rdquo; or &ldquo;Show me the pressing index over time&rdquo;
            </p>
            {tables.length > 0 && (
              <p className="muted" style={{ fontSize: 11 }}>
                {tables.length} tables available &mdash;{' '}
                <button className="linklike" style={{ fontSize: 11 }}
                        onClick={() => setShowTables(true)}>
                  view schema
                </button>
              </p>
            )}
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`pg-chat__msg pg-chat__msg--${msg.role}`}>
            <div className="pg-chat__bubble">
              {msg.role === 'assistant' && msg.sql ? (
                <>
                  <p className="pg-chat__explanation">
                    {msg.explanation || msg.content}
                  </p>
                  {/* SQL is for the curious, not the headline: the
                      explanation leads and the query sits behind a toggle. */}
                  <details className="pg-chat__sql-details">
                    <summary>Show SQL</summary>
                    <pre className="pg-chat__sql">{msg.sql}</pre>
                  </details>
                  <div className="pg-chat__actions">
                    {onAddCard ? (
                      <button
                        className="adm-btn"
                        onClick={() => {
                          const userQ = messages.slice(0, i).reverse().find((m) => m.role === 'user')
                          onAddCard(userQ?.content ?? msg.explanation ?? 'Chat query', msg.sql!, msg.visual, msg.explanation)
                        }}
                      >
                        Add as card
                      </button>
                    ) : (
                      <button
                        className="adm-btn"
                        onClick={() => onUseQuery?.(msg.sql!, msg.visual)}
                      >
                        Use this query
                      </button>
                    )}
                    {/* "Remember this" is hidden until learned examples
                        persist somewhere durable: today they land in a JSON
                        file on the instance's ephemeral disk and silently
                        die with it (SDET 2026-09-09, UI-2). The real fix —
                        a table + a verbatim POST /playground/learned-examples
                        — needs a migration, which is Ben-gated. */}
                  </div>
                  {msg.suggestions && msg.suggestions.length > 0 && (
                    <div className="pg-chat__suggestions">
                      {msg.suggestions.map((s, j) => (
                        <button
                          key={j}
                          className="pg-chat__suggestion"
                          onClick={() => setInput(s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p>{msg.content}</p>
                  {msg.tempTable && (
                    <div className="pg-chat__actions">
                      <button
                        className="adm-btn"
                        onClick={() => setInput(`SELECT * FROM ${msg.tempTable}`)}
                      >
                        Use this table
                      </button>
                      <button
                        className="linklike"
                        onClick={() => {
                          void navigator.clipboard.writeText(msg.tempTable!)
                        }}
                        title="Copy table name to clipboard"
                      >
                        Copy name
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="pg-chat__msg pg-chat__msg--assistant">
            <div className="pg-chat__bubble pg-chat__thinking">Thinking...</div>
          </div>
        )}
      </div>

      {error && <div className="pg-chat__error">{error}</div>}

      <div className="pg-chat__input">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder={
            gameCode
              ? 'Ask about this game\u2019s data...'
              : 'Select a game first'
          }
          disabled={!gameCode || sending}
          rows={2}
        />
        <button
          className="adm-btn"
          onClick={() => void send()}
          disabled={!input.trim() || !gameCode || sending}
        >
          Send
        </button>
      </div>
    </div>
  )
}
